import { useContext, useMemo } from 'react';
import { Box, LinearProgress, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format, formatDistanceToNow } from 'date-fns';
import { ArrowRight, CalendarPlus, Lightning } from '@phosphor-icons/react';
import { CalendarDayContext } from '@/contexts/CalendarDayContext';
import { getChildActivityStatus, getNextActivity } from '@/components/child';
import { getRoutineStepLabel } from '@/utils/routine';
import { useRoutines } from '@/contexts/RoutineContext';
import { dateLocales } from '@/i18n/dateLocales';
import { useThemePreference } from '@/contexts/ThemePreferenceContext';
import { getThemeTokens } from '@/utils/appThemes';

/**
 * DayOverview — den vuxna brukarens överblick överst på dagsidan.
 *
 * Bakgrund: vuxenvyn var fyra listor rakt ner (pågående, kommande, missade, klara)
 * under en hälsning. Den sade ingenting om dagen som helhet: man fick läsa listan för
 * att veta vad som var på gång, och ingenting alls när dagen var tom. Barn- och
 * tonårsvyerna hade sedan länge både ett "nästa upp" och en dagsprogress — den vuxna
 * brukaren hade minst stöd av alla tre, i samma app och för samma sorts dag.
 *
 * Tre saker, i den ordning de behövs:
 *   1. NÄSTA UPP — en enda handling att gå vidare med. Igångsättningsstöd är inte
 *      något man växer ifrån; det är bara tonen som ska vara en annan än barnvyns.
 *   2. DAGENS PROGRESS — hur långt dagen har kommit, utan att man räknar rader.
 *   3. TOMT LÄGE — när dagen är tom står det att den är tom, i stället för fyra
 *      rubriker som alla saknar innehåll.
 *
 * Medvetet nyktert: inga emojier, ingen neon, inga stjärnor. Vuxenprofilen har
 * belöningarna avstängda, och en dagsprogress som ser ut som en belöning vore något
 * annat än det den är här — en upplysning om var man är.
 */

const DayOverview = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { instances } = useContext(CalendarDayContext);
  const { getRoutine } = useRoutines();
  const { selectedTheme, isDarkMode } = useThemePreference();
  const tk = getThemeTokens(selectedTheme, isDarkMode);

  const locale = dateLocales[i18n.language.split('-')[0]] ?? dateLocales.en;

  const nextUp = useMemo(() => getNextActivity(instances), [instances]);
  const doneCount = useMemo(
    () => instances.filter((instance) => getChildActivityStatus(instance) === 'done').length,
    [instances],
  );

  const total = instances.length;
  const progress = total > 0 ? (doneCount / total) * 100 : 0;

  if (total === 0) {
    return (
      <Box
        sx={{
          border: `1px dashed ${tk.border}`,
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          color: tk.textSoft,
        }}
      >
        <CalendarPlus size={32} weight="duotone" color={tk.textSoft} />
        <Typography sx={{ fontWeight: 600, mt: 1 }}>{t('daily.overview.emptyTitle')}</Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {t('daily.overview.emptyBody')}
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {nextUp && (
        <Box
          component="button"
          type="button"
          onClick={() => navigate(`/activity/${nextUp.instance.activityInstanceId}`)}
          sx={{
            width: '100%',
            textAlign: 'left',
            fontFamily: 'inherit',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            backgroundColor: `${tk.primary}1A`,
            border: '1px solid',
            borderColor: `${tk.primary}59`,
            borderRadius: 2,
            p: 2,
            '&:focus-visible': { outline: '2px solid currentColor', outlineOffset: '2px' },
          }}
        >
          <Box
            aria-hidden
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              bgcolor: 'primary.main',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <Box
              component={Lightning}
              size={20}
              weight="fill"
              sx={{ color: 'primary.contrastText' }}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: tk.primary,
                fontWeight: 700,
                lineHeight: 1.4,
              }}
            >
              {t('daily.overview.nextUp')}
            </Typography>
            {/* Är nästa sak en pågående rutin står STEGET här, inte bara rutinens namn —
                "Morgonrutin · Borsta tänderna" säger vad man ska göra, "Morgonrutin" bara
                var man är. Samma etikett som veckoschemat och barnvyns kort använder. */}
            <Typography sx={{ fontWeight: 700 }} noWrap>
              {getRoutineStepLabel(nextUp.instance, getRoutine(nextUp.instance))}
            </Typography>
            {/* Ungefärlig tid först, klockslaget efter, längden sist — samma ordning
                som tonåringens kort. "Om 40 minuter" svarar på frågan man ställer sig;
                "kl 15:50" är det man behöver när svaret är "snart". */}
            <Typography variant="body2" sx={{ color: tk.textSoft }}>
              {formatDistanceToNow(new Date(nextUp.instance.startDate), {
                addSuffix: true,
                locale,
              })}
              {' · '}
              {t('daily.overview.atTime', {
                time: format(new Date(nextUp.instance.startDate), 'HH:mm'),
              })}
              {nextUp.instance.duration > 0 &&
                ` · ${nextUp.instance.duration} ${t('daily.overview.min')}`}
            </Typography>
          </Box>
          <ArrowRight size={20} weight="bold" color={tk.textSoft} />
        </Box>
      )}

      <Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="baseline"
          sx={{ mb: 0.5 }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {t('daily.overview.progressTitle')}
          </Typography>
          <Typography variant="body2" sx={{ color: tk.textSoft, fontWeight: 600 }}>
            {t('daily.overview.progressCount', { done: doneCount, total })}
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={progress}
          aria-label={t('daily.overview.progressCount', { done: doneCount, total }) as string}
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Box>
    </Stack>
  );
};

/**
 * CalmCard — det lugna avslutet på den vuxna brukarens dag.
 *
 * Ytan under listorna stod tom. Tonåringen fick där ett uppmuntranskort; vuxenvyn ska
 * inte ha samma sak, för den har inga stjärnor att fira med och ingen ska peppas som ett
 * barn. Det som hör hemma här är i stället ett påminnande om att dagen får ta den tid
 * den tar — och, konkret, vad nästa sak är och när.
 *
 * Ingen gamification: ingen poäng, ingen streak, ingen nivå. Bara en mening.
 */
export const CalmCard = () => {
  const { t, i18n } = useTranslation();
  const { instances } = useContext(CalendarDayContext);
  const { selectedTheme, isDarkMode } = useThemePreference();
  const tk = getThemeTokens(selectedTheme, isDarkMode);

  const nextUp = useMemo(() => getNextActivity(instances), [instances]);
  const locale = dateLocales[i18n.language.split('-')[0]] ?? dateLocales.en;

  if (instances.length === 0) return null;

  return (
    <Box
      sx={{
        backgroundColor: tk.card,
        border: `1px solid ${tk.border}`,
        borderRadius: 2,
        p: 2.25,
        textAlign: 'center',
      }}
    >
      <Typography sx={{ fontSize: '1.4rem', lineHeight: 1, mb: 1 }}>🌤️</Typography>
      <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: tk.text, mb: 0.5 }}>
        {t('daily.calm.title')}
      </Typography>
      <Typography variant="body2" sx={{ color: tk.textSoft, lineHeight: 1.5 }}>
        {nextUp
          ? t('daily.calm.bodyWithNext', {
              name: nextUp.instance.activityName,
              time: format(new Date(nextUp.instance.startDate), 'HH:mm', { locale }),
            })
          : t('daily.calm.body')}
      </Typography>
    </Box>
  );
};

export default DayOverview;
